import { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import {
  Order,
  OrderType,
  getOrdersForToken,
  createOrder,
  cancelOrder,
  deleteOrder,
  formatOrderType,
  getOrderTypeColor,
  checkOrderTriggers,
  checkDcaOrders,
} from '../lib/orders';

interface OrderPanelProps {
  tokenAddress: string;
  tokenSymbol: string;
  currentPriceUsd: number;
  onOrderTriggered?: (order: Order) => void;
}

export function OrderPanel({ tokenAddress, tokenSymbol, currentPriceUsd, onOrderTriggered }: OrderPanelProps) {
  const { publicKey } = useWallet();
  const [orders, setOrders] = useState<Order[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [orderType, setOrderType] = useState<OrderType>('limit_buy');
  const [triggerPrice, setTriggerPrice] = useState('');
  const [amount, setAmount] = useState('');
  const [expiresIn, setExpiresIn] = useState('24'); // hours
  // DCA specific state
  const [dcaInterval, setDcaInterval] = useState('60'); // minutes
  const [dcaCount, setDcaCount] = useState('5'); // number of purchases

  // Load orders on mount
  useEffect(() => {
    setOrders(getOrdersForToken(tokenAddress));
  }, [tokenAddress]);

  // Check for triggered orders when price changes
  useEffect(() => {
    if (currentPriceUsd > 0) {
      const triggered = checkOrderTriggers(tokenAddress, currentPriceUsd);
      if (triggered.length > 0) {
        setOrders(getOrdersForToken(tokenAddress));
        triggered.forEach(order => {
          onOrderTriggered?.(order);
        });
      }
    }

    // Also check for DCA orders that are due
    const dcaDue = checkDcaOrders(tokenAddress);
    if (dcaDue.length > 0) {
      setOrders(getOrdersForToken(tokenAddress));
      dcaDue.forEach(order => {
        onOrderTriggered?.(order);
      });
    }
  }, [tokenAddress, currentPriceUsd, onOrderTriggered]);

  const handleCreateOrder = () => {
    if (!publicKey || !amount) return;

    // For non-DCA orders, require trigger price
    if (orderType !== 'dca' && !triggerPrice) return;

    const expiresAt = expiresIn && orderType !== 'dca'
      ? Date.now() + parseInt(expiresIn) * 60 * 60 * 1000
      : undefined;

    const dcaIntervalNum = parseInt(dcaInterval) || 60;
    const dcaCountNum = parseInt(dcaCount) || 5;

    createOrder({
      type: orderType,
      tokenAddress,
      tokenSymbol,
      triggerPrice: orderType === 'dca' ? 0 : parseFloat(triggerPrice),
      amount: parseFloat(amount),
      walletAddress: publicKey.toString(),
      expiresAt,
      // DCA-specific fields
      ...(orderType === 'dca' && {
        dcaInterval: dcaIntervalNum,
        dcaRemaining: dcaCountNum,
        dcaTotal: dcaCountNum,
        dcaNextAt: Date.now() + dcaIntervalNum * 60 * 1000, // First execution after interval
      }),
    });

    setOrders(getOrdersForToken(tokenAddress));
    setShowCreateForm(false);
    setTriggerPrice('');
    setAmount('');
  };

  const handleCancelOrder = (orderId: string) => {
    cancelOrder(orderId);
    setOrders(getOrdersForToken(tokenAddress));
  };

  const handleDeleteOrder = (orderId: string) => {
    deleteOrder(orderId);
    setOrders(getOrdersForToken(tokenAddress));
  };

  const pendingOrders = orders.filter(o => o.status === 'pending');
  const historyOrders = orders.filter(o => o.status !== 'pending');

  const priceChangePercent = triggerPrice
    ? ((parseFloat(triggerPrice) - currentPriceUsd) / currentPriceUsd * 100).toFixed(1)
    : null;

  return (
    <div className="card" style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 600 }}>Orders</h3>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          style={{
            padding: '6px 12px',
            fontSize: '12px',
            background: showCreateForm ? 'var(--bg-secondary)' : 'var(--accent)',
            color: showCreateForm ? 'var(--text-primary)' : '#000',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
          }}
        >
          {showCreateForm ? 'Cancel' : '+ New Order'}
        </button>
      </div>

      {/* Create Order Form */}
      {showCreateForm && (
        <div style={{
          padding: '12px',
          background: 'var(--bg-secondary)',
          borderRadius: 'var(--radius-md)',
          marginBottom: '12px',
        }}>
          {/* Order Type Tabs */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
            {(['limit_buy', 'limit_sell', 'stop_loss', 'take_profit', 'dca'] as OrderType[]).map((type) => (
              <button
                key={type}
                onClick={() => setOrderType(type)}
                style={{
                  flex: 1,
                  padding: '6px 4px',
                  fontSize: '10px',
                  fontWeight: orderType === type ? 600 : 400,
                  background: orderType === type ? getOrderTypeColor(type) : 'transparent',
                  color: orderType === type ? '#000' : 'var(--text-secondary)',
                  border: `1px solid ${orderType === type ? getOrderTypeColor(type) : 'var(--border)'}`,
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                }}
              >
                {formatOrderType(type).replace(' ', '\n')}
              </button>
            ))}
          </div>

          {/* Current Price Reference */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '11px',
            color: 'var(--text-tertiary)',
            marginBottom: '8px',
          }}>
            <span>Current Price</span>
            <span style={{ fontFamily: 'var(--font-mono)' }}>
              ${currentPriceUsd.toFixed(8)}
            </span>
          </div>

          {/* DCA-specific fields */}
          {orderType === 'dca' ? (
            <>
              <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                  Buy Interval
                </label>
                <select
                  value={dcaInterval}
                  onChange={(e) => setDcaInterval(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    fontSize: '13px',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <option value="15">Every 15 minutes</option>
                  <option value="30">Every 30 minutes</option>
                  <option value="60">Every hour</option>
                  <option value="360">Every 6 hours</option>
                  <option value="1440">Every day</option>
                  <option value="10080">Every week</option>
                </select>
              </div>

              <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                  Number of Purchases
                </label>
                <select
                  value={dcaCount}
                  onChange={(e) => setDcaCount(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    fontSize: '13px',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <option value="3">3 purchases</option>
                  <option value="5">5 purchases</option>
                  <option value="10">10 purchases</option>
                  <option value="20">20 purchases</option>
                  <option value="50">50 purchases</option>
                </select>
              </div>

              <div style={{
                padding: '8px',
                background: 'var(--accent-muted)',
                borderRadius: 'var(--radius-sm)',
                marginBottom: '8px',
                fontSize: '11px',
                color: 'var(--accent)',
              }}>
                Total: {parseFloat(amount || '0') * parseInt(dcaCount)} SOL over {
                  parseInt(dcaInterval) >= 1440
                    ? `${Math.round(parseInt(dcaInterval) * parseInt(dcaCount) / 1440)} days`
                    : parseInt(dcaInterval) >= 60
                    ? `${Math.round(parseInt(dcaInterval) * parseInt(dcaCount) / 60)} hours`
                    : `${parseInt(dcaInterval) * parseInt(dcaCount)} minutes`
                }
              </div>
            </>
          ) : (
            /* Trigger Price Input (for non-DCA orders) */
            <div style={{ marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Trigger Price (USD)
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="number"
                  value={triggerPrice}
                  onChange={(e) => setTriggerPrice(e.target.value)}
                  placeholder="0.00000000"
                  step="0.00000001"
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    paddingRight: priceChangePercent ? '60px' : '10px',
                    fontSize: '13px',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    color: 'var(--text-primary)',
                  }}
                />
                {priceChangePercent && (
                  <span style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '11px',
                  color: parseFloat(priceChangePercent) >= 0 ? 'var(--success)' : 'var(--error)',
                }}>
                  {parseFloat(priceChangePercent) >= 0 ? '+' : ''}{priceChangePercent}%
                </span>
              )}
            </div>
          </div>
          )}

          {/* Amount Input */}
          <div style={{ marginBottom: '8px' }}>
            <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
              {orderType === 'dca' ? 'Amount per Purchase (SOL)' : orderType === 'limit_buy' ? 'Amount (SOL)' : `Amount (${tokenSymbol})`}
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              step="0.01"
              style={{
                width: '100%',
                padding: '8px 10px',
                fontSize: '13px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
              }}
            />
          </div>

          {/* Expires In (not for DCA) */}
          {orderType !== 'dca' && (
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Expires In (hours)
              </label>
              <select
                value={expiresIn}
                onChange={(e) => setExpiresIn(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  fontSize: '13px',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--text-primary)',
                }}
              >
                <option value="1">1 hour</option>
                <option value="6">6 hours</option>
                <option value="24">24 hours</option>
                <option value="72">3 days</option>
                <option value="168">1 week</option>
                <option value="">Never</option>
              </select>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleCreateOrder}
            disabled={!publicKey || !amount || (orderType !== 'dca' && !triggerPrice)}
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '13px',
              fontWeight: 600,
              background: getOrderTypeColor(orderType),
              color: '#000',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: publicKey && amount && (orderType === 'dca' || triggerPrice) ? 'pointer' : 'not-allowed',
              opacity: publicKey && amount && (orderType === 'dca' || triggerPrice) ? 1 : 0.5,
            }}
          >
            {orderType === 'dca' ? 'Start DCA' : `Create ${formatOrderType(orderType)}`}
          </button>

          {!publicKey && (
            <p style={{ fontSize: '11px', color: 'var(--warning)', marginTop: '8px', textAlign: 'center' }}>
              Connect wallet to create orders
            </p>
          )}
        </div>
      )}

      {/* Pending Orders */}
      {pendingOrders.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>
            Active Orders ({pendingOrders.length})
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {pendingOrders.map((order) => (
              <div
                key={order.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 10px',
                  background: 'var(--bg-secondary)',
                  borderRadius: 'var(--radius-sm)',
                  borderLeft: `3px solid ${getOrderTypeColor(order.type)}`,
                }}
              >
                <div>
                  <p style={{ fontSize: '12px', fontWeight: 500, marginBottom: '2px' }}>
                    {formatOrderType(order.type)}
                    {order.type === 'dca' && order.dcaRemaining && order.dcaTotal && (
                      <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>
                        {' '}({order.dcaTotal - order.dcaRemaining}/{order.dcaTotal})
                      </span>
                    )}
                  </p>
                  <p style={{ fontSize: '10px', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
                    {order.type === 'dca' ? (
                      <>
                        {order.amount} SOL every {
                          order.dcaInterval && order.dcaInterval >= 1440
                            ? `${order.dcaInterval / 1440} day(s)`
                            : order.dcaInterval && order.dcaInterval >= 60
                            ? `${order.dcaInterval / 60} hr(s)`
                            : `${order.dcaInterval} min`
                        }
                      </>
                    ) : (
                      <>@ ${order.triggerPrice.toFixed(8)} • {order.amount} {order.type === 'limit_buy' ? 'SOL' : tokenSymbol}</>
                    )}
                  </p>
                </div>
                <button
                  onClick={() => handleCancelOrder(order.id)}
                  style={{
                    padding: '4px 8px',
                    fontSize: '10px',
                    background: 'var(--error)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Order History */}
      {historyOrders.length > 0 && (
        <div>
          <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>
            History ({historyOrders.length})
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {historyOrders.slice(0, 5).map((order) => (
              <div
                key={order.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '6px 8px',
                  background: 'var(--bg-secondary)',
                  borderRadius: 'var(--radius-sm)',
                  opacity: 0.7,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    padding: '2px 4px',
                    fontSize: '9px',
                    fontWeight: 500,
                    background: order.status === 'executed' ? 'var(--success)'
                      : order.status === 'cancelled' ? 'var(--error)'
                      : 'var(--warning)',
                    color: '#000',
                    borderRadius: '2px',
                  }}>
                    {order.status.toUpperCase()}
                  </span>
                  <span style={{ fontSize: '11px' }}>
                    {formatOrderType(order.type)}
                  </span>
                </div>
                <button
                  onClick={() => handleDeleteOrder(order.id)}
                  style={{
                    padding: '2px 6px',
                    fontSize: '9px',
                    background: 'transparent',
                    color: 'var(--text-tertiary)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {orders.length === 0 && !showCreateForm && (
        <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', textAlign: 'center', padding: '12px 0' }}>
          No orders yet. Create a limit order to buy or sell at a specific price.
        </p>
      )}
    </div>
  );
}
